(function () {
  // Map logical keys to badge name fragments. Each value can be a string
  // (single name) or an array of strings (multiple acceptable names — first
  // matching checkbox wins). The matcher uses titleMatches() which is
  // case- and whitespace-insensitive and accepts startsWith semantics, so
  // a single entry already tolerates trailing addon-charge suffixes like
  // "+6,00€". Use an array only when the same logical badge can appear
  // under genuinely different label texts (e.g. campaign rename, alternate
  // spelling with/without diacritics).
  const BADGE_TITLES = {
    LIGA: ["Badge Liga", "Liga Betclic"],
    CAMPEAO: ["Campeão Liga", "Badge Campeao Nacional"],
    TACA: "Badge Quinas",
    UCL: "Badge UCL",
    FUNDACAO: "Fundação UCL",
  };

  const ALLOWED_COMBOS_BY_KEY = [
    ["LIGA"],
    ["CAMPEAO"],
    ["TACA"],
    ["UCL"],
    ["FUNDACAO"],
    ["CAMPEAO", "LIGA"],
    ["TACA", "UCL"],
    ["TACA", "UCL", "FUNDACAO"],
    ["TACA", "FUNDACAO"],
    ["UCL", "FUNDACAO"],
  ];

  // Badge keys to always render as disabled, regardless of the combo
  // allow-list above. Use when an add-on (e.g. patch / pin) is out of
  // stock at the supplier and we don't have real-time inventory data
  // for these TeeInBlue clipart options. Keys must match BADGE_TITLES.
  // Leave empty to disable nothing (default).
  // const OUT_OF_STOCK_BADGE_KEYS = ["LIGA"];
  const OUT_OF_STOCK_BADGE_KEYS = [];

  // Subset of badge keys that, when ticked, stamp a pre-positioned PNG over
  // the first slide of the PDP gallery. Other keys (LIGA / UCL / FUNDACAO)
  // are intentionally absent and therefore never render an overlay. CAMPEAO
  // and TACA are mutually exclusive in ALLOWED_COMBOS_BY_KEY above, so a
  // single overlay slot is sufficient (no need to render two at once).
  const BADGE_OVERLAY_IMAGES = {
    CAMPEAO:
      "https://cdn.shopify.com/s/files/1/0889/0269/7293/files/Badge_Campe_o.png?v=1778625576",
    TACA: "https://cdn.shopify.com/s/files/1/0889/0269/7293/files/Badge_Ta_a.png?v=1778625561",
  };

  // Resolves the effective overlay image for each key. Per-product overrides
  // set by Liquid via window.scpBadgeOverlayOverrides (sourced from
  // product.metafields.scp.badges_frente_override: position 0 = TACA,
  // position 1 = CAMPEAO) win per-key; otherwise we fall back to the
  // hardcoded defaults above. Read at call time so a late hydration of
  // the global still takes effect on the next observer tick.
  let lastLoggedSourceSig = null;
  function getEffectiveOverlayImages() {
    const overrides =
      (typeof window !== "undefined" && window.scpBadgeOverlayOverrides) || {};
    const out = {};
    const sources = {};
    Object.keys(BADGE_OVERLAY_IMAGES).forEach(function (key) {
      if (overrides[key]) {
        out[key] = overrides[key];
        sources[key] = "override metafield";
      } else {
        out[key] = BADGE_OVERLAY_IMAGES[key];
        sources[key] = "default";
      }
    });

    // Dedupe console logs: only re-log when the resolved source set changes
    // (e.g. on first read, or when window.scpBadgeOverlayOverrides hydrates
    // late). The observers in this file call this function on every tick,
    // so without dedupe we'd spam the console on every UI mutation.
    const sig = JSON.stringify(sources);
    if (sig !== lastLoggedSourceSig) {
      lastLoggedSourceSig = sig;
      Object.keys(sources).forEach(function (key) {
        /*console.log(
          "[scp-tb-badge-overlay] " + key + ": using " + sources[key],
        );*/
      });
    }

    return out;
  }

  // Normalize a title for matching: trim, collapse whitespace, lowercase
  function normalize(str) {
    return (str || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  // Check if a badge's title matches a configured name
  // Matches if the title starts with the configured name (case/whitespace insensitive)
  function titleMatches(badgeTitle, configuredName) {
    const a = normalize(badgeTitle);
    const b = normalize(configuredName);
    return a === b || a.startsWith(b + " ") || a.startsWith(b);
  }

  function getCheckboxes(container) {
    return container.querySelectorAll(
      'input.tee-checkbox-input[type="checkbox"]',
    );
  }

  // Build a key->value map by matching configured names against actual
  // data-titles. Lifted to module scope so the gallery-rebuild observer
  // (see below) can call it without re-running initBadgeSection.
  function buildKeyToValueMap(container) {
    const map = {};
    const checkboxes = getCheckboxes(container);

    for (const [key, configured] of Object.entries(BADGE_TITLES)) {
      const candidates = Array.isArray(configured) ? configured : [configured];
      let found = null;
      for (const candidate of candidates) {
        for (const cb of checkboxes) {
          const label = container.querySelector(`label[for="${cb.id}"]`);
          if (!label) continue;
          const title = label.getAttribute("data-title");
          if (titleMatches(title, candidate)) {
            found = cb.value;
            break;
          }
        }
        if (found) break;
      }
      if (found) {
        map[key] = found;
      } else {
        console.warn(
          "[badge] Could not find badge matching " +
            JSON.stringify(candidates) +
            " (key: " +
            key +
            ")",
        );
      }
    }
    return map;
  }

  function getSelection(container) {
    const selected = new Set();
    getCheckboxes(container).forEach((cb) => {
      if (cb.getAttribute("aria-checked") === "true") selected.add(cb.value);
    });
    return selected;
  }

  // Inject the overlay positioning rules exactly once (idempotent via a
  // flag on the function). Kept inline rather than in a CSS file so this
  // feature is fully self-contained inside teeinblue-custom.js.
  function ensureBadgeOverlayStyles() {
    if (ensureBadgeOverlayStyles._injected) return;
    ensureBadgeOverlayStyles._injected = true;
    const style = document.createElement("style");
    style.setAttribute("data-scp-tb-badge-overlay-styles", "");
    style.textContent = [
      // Defensive: Swiper already positions slides, but Shopify's gallery
      // layouts vary, so this guarantees the absolute child anchors to
      // the slide box.
      ".m-product-media--item.swiper-slide { position: relative; }",
      ".scp-tb-badge-overlay {",
      "  position: absolute;",
      "  inset: 0;",
      "  width: 100%;",
      "  height: 100%;",
      // The badge PNG is pre-positioned within its own canvas, so contain
      // (not cover) keeps it aligned 1:1 with the underlying product
      // image regardless of slide aspect ratio.
      "  object-fit: contain;",
      "  pointer-events: none;",
      "  z-index: 5;",
      "}",
    ].join("\n");
    (document.head || document.documentElement).appendChild(style);
  }

  // Stamp / update / remove the badge overlay on the first PDP gallery
  // slide based on the current selection. CAMPEAO and TACA are mutually
  // exclusive in ALLOWED_COMBOS_BY_KEY, so first-match wins is safe.
  function applyBadgeOverlay(container, keyToValue, selection) {
    ensureBadgeOverlayStyles();

    const overlayImages = getEffectiveOverlayImages();
    let overlayUrl = null;
    for (const key of Object.keys(overlayImages)) {
      const value = keyToValue[key];
      if (value && selection.has(value)) {
        overlayUrl = overlayImages[key];
        break;
      }
    }

    // Prefer the PDP main gallery scope; fall back to any .m-media-gallery
    // on the page so this still works on alternative templates that wrap
    // the gallery differently.
    const gallery =
      document.querySelector(".m-main-product--media .m-media-gallery") ||
      document.querySelector(".m-media-gallery");
    const firstSlide =
      gallery &&
      gallery.querySelector(
        '.m-product-media--item.swiper-slide[data-index="0"]',
      );
    if (!firstSlide) return;

    let overlay = firstSlide.querySelector(":scope > .scp-tb-badge-overlay");

    if (!overlayUrl) {
      if (overlay) overlay.remove();
      return;
    }

    if (!overlay) {
      overlay = document.createElement("img");
      overlay.className = "scp-tb-badge-overlay";
      overlay.setAttribute("aria-hidden", "true");
      overlay.alt = "";
      firstSlide.appendChild(overlay);
    } else if (overlay.parentElement !== firstSlide) {
      // Defensive: a gallery rebuild can detach the overlay from the new
      // slide-0 node while the element itself remains live.
      firstSlide.appendChild(overlay);
    }

    if (overlay.getAttribute("src") !== overlayUrl) {
      overlay.setAttribute("src", overlayUrl);
    }
  }

  function initBadgeSection(container) {
    if (container.dataset.badgeLogicInit === "1") return;
    container.dataset.badgeLogicInit = "1";

    function resolveAllowedCombos(keyToValue) {
      const resolved = [];
      for (const combo of ALLOWED_COMBOS_BY_KEY) {
        const values = new Set();
        let valid = true;
        for (const key of combo) {
          const value = keyToValue[key];
          if (!value) {
            valid = false;
            break;
          }
          values.add(value);
        }
        if (valid) resolved.push(values);
      }
      return resolved;
    }

    // Lifted to the initBadgeSection scope so the OOS helper below sees
    // the freshest mapping after each MutationObserver-driven rebuild
    // (TeeInBlue can swap the checkbox values when re-rendering layers).
    let keyToValue = buildKeyToValueMap(container);
    let allowedCombos = resolveAllowedCombos(keyToValue);

    function canAdd(currentSelection, candidateValue) {
      const hypothetical = new Set(currentSelection);
      hypothetical.add(candidateValue);
      return allowedCombos.some((combo) => {
        if (combo.size < hypothetical.size) return false;
        for (const v of hypothetical) if (!combo.has(v)) return false;
        return true;
      });
    }

    // Resolve OUT_OF_STOCK_BADGE_KEYS to checkbox values via the current
    // keyToValue mapping. Returns true when the candidate checkbox value
    // belongs to a key flagged as out of stock.
    function isOutOfStockValue(value) {
      for (const key of OUT_OF_STOCK_BADGE_KEYS) {
        if (keyToValue[key] === value) return true;
      }
      return false;
    }

    function updateDisabledStates() {
      const selection = getSelection(container);

      getCheckboxes(container).forEach((cb) => {
        const isSelected = selection.has(cb.value);
        const shouldDisable =
          !isSelected &&
          (isOutOfStockValue(cb.value) || !canAdd(selection, cb.value));

        const clipartEl = cb.closest(".tee-clipart");
        const labelEl = container.querySelector(`label[for="${cb.id}"]`);

        if (shouldDisable) {
          cb.disabled = true;
          cb.setAttribute("aria-disabled", "true");
          if (clipartEl) clipartEl.classList.add("tee-clipart--disabled");
          if (labelEl) {
            labelEl.style.opacity = "0.3";
            labelEl.style.pointerEvents = "none";
            labelEl.style.cursor = "not-allowed";
          }
        } else {
          cb.disabled = false;
          cb.removeAttribute("aria-disabled");
          if (clipartEl) clipartEl.classList.remove("tee-clipart--disabled");
          if (labelEl) {
            labelEl.style.opacity = "";
            labelEl.style.pointerEvents = "";
            labelEl.style.cursor = "";
          }
        }
      });
    }

    const observer = new MutationObserver(() => {
      clearTimeout(observer._t);
      observer._t = setTimeout(() => {
        keyToValue = buildKeyToValueMap(container);
        allowedCombos = resolveAllowedCombos(keyToValue);
        updateDisabledStates();
        applyBadgeOverlay(container, keyToValue, getSelection(container));
      }, 20);
    });

    getCheckboxes(container).forEach((cb) => {
      observer.observe(cb, {
        attributes: true,
        attributeFilter: ["aria-checked", "value"],
      });
    });

    updateDisabledStates();

    // Paint any pre-selected badge overlay on first init. TeeInBlue can
    // restore prior state asynchronously, so this also gets re-asserted by
    // the observer above on the first aria-checked mutation.
    const initialKeyToValue = buildKeyToValueMap(container);
    applyBadgeOverlay(container, initialKeyToValue, getSelection(container));
  }

  function initAll() {
    document
      .querySelectorAll(".s-personalization-badge")
      .forEach(initBadgeSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  const pageObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (
          node.classList &&
          node.classList.contains("s-personalization-badge")
        ) {
          initBadgeSection(node);
        } else if (node.querySelectorAll) {
          node
            .querySelectorAll(".s-personalization-badge")
            .forEach(initBadgeSection);
        }
      });
    }
  });
  pageObserver.observe(document.body, { childList: true, subtree: true });

  /* ============================================================
   Gallery-rebuild resilience for badge overlay

   Shopify regenerates the PDP gallery DOM on variant changes, which
   detaches any previously-painted badge overlay. Re-assert it for every
   already-initialised .s-personalization-badge container after each
   coalesced burst of body mutations. Only initialised containers
   (badgeLogicInit === "1") trigger work, so this stays cheap on pages
   without a personalisation form.
   ============================================================ */

  let galleryRescanScheduled = false;
  const galleryObserver = new MutationObserver(() => {
    if (galleryRescanScheduled) return;
    galleryRescanScheduled = true;
    setTimeout(() => {
      galleryRescanScheduled = false;
      document.querySelectorAll(".s-personalization-badge").forEach((c) => {
        if (c.dataset.badgeLogicInit !== "1") return;
        const keyToValue = buildKeyToValueMap(c);
        applyBadgeOverlay(c, keyToValue, getSelection(c));
      });
    }, 60);
  });
  galleryObserver.observe(document.body, { childList: true, subtree: true });

  /* ============================================================
   Addon charge auto-toggle (tied to name/number inputs)

   Per-form initialisation, bubble-phase listeners bound directly to
   each Personalização form's NOME / NÚMERO inputs. A debounced
   MutationObserver re-scans .tee-campaign-form-wrapper nodes so
   freshly-rendered forms (post variant change / template switch) pick
   up listeners. Decoupled from the price-sync RAF on purpose -- see
   plan revert_addon-charge_subsystem.
   ============================================================ */

  function getClickTarget(radio) {
    if (!radio) return null;
    var label = radio.id
      ? document.querySelector(
          'label[for="' + radio.id.replace(/"/g, '\\"') + '"]',
        )
      : null;
    var teeRadio = radio.closest(".tee-radio");
    return label || teeRadio || radio;
  }

  function clickWithoutStealingFocus(target) {
    if (!target) return;
    var prevActive = document.activeElement;
    var selStart = null;
    var selEnd = null;
    var canRestoreSelection = !!(
      prevActive &&
      typeof prevActive.selectionStart === "number" &&
      typeof prevActive.setSelectionRange === "function"
    );

    if (canRestoreSelection) {
      try {
        selStart = prevActive.selectionStart;
        selEnd = prevActive.selectionEnd;
      } catch (e) {
        canRestoreSelection = false;
      }
    }

    // Vue / TeeInBlue's @click handler is bound to the .tee-radio wrapper of
    // the addon-charge field but no-ops when an ancestor .tee-field carries
    // s-personalization-hidden (display: none). Vue treats those elements as
    // non-interactive and skips its reactive update, leaving radio.checked
    // out of sync with its store (and .tee-price-addons unrendered).
    // Synchronously strip the marker for the click and restore it immediately
    // so Vue processes the event without exposing the field visually.
    var hiddenAncestor =
      target.closest && target.closest(".s-personalization-hidden");
    if (hiddenAncestor) {
      hiddenAncestor.classList.remove("s-personalization-hidden");
      try {
        target.click();
      } finally {
        hiddenAncestor.classList.add("s-personalization-hidden");
      }
    } else {
      target.click();
    }

    if (
      prevActive &&
      prevActive !== document.activeElement &&
      typeof prevActive.focus === "function"
    ) {
      try {
        prevActive.focus({ preventScroll: true });
      } catch (e) {
        prevActive.focus();
      }
      if (canRestoreSelection && selStart !== null && selEnd !== null) {
        try {
          prevActive.setSelectionRange(selStart, selEnd);
        } catch (e) {
          // non-text inputs (e.g. number on some browsers) reject setSelectionRange
        }
      }
    }
  }

  function syncAddonChargeForForm(form) {
    if (!form) return;
    var activeNodes = getActiveAddonChargeNodes(form);
    var currentName = activeNodes.nameInput;
    var currentNumber = activeNodes.numberInput;
    var currentRadio = activeNodes.addonRadio;

    if (!currentRadio || (!currentName && !currentNumber)) {
      return;
    }

    var nameValue = currentName ? currentName.value : "";
    var numberValue = currentNumber ? currentNumber.value : "";
    var trimmedLength = (nameValue + numberValue).trim().length;
    var shouldBeChecked = trimmedLength > 0;

    // Vue is the source of truth for whether the addon is *really* selected.
    // The native radio.checked can drift true after a hide/click/show click
    // that landed before Vue had mounted the freshly rebuilt .tee-radio
    // wrapper (e.g. immediately after a template round-trip): the browser
    // toggles `checked` via the label-for relationship, but Vue's @click
    // never fires, so `layer-275953` stays null and `.tee-price-addons` is
    // never rendered. Decide based on `.tee-radio.active` so the deferred
    // template-radio sync (and any subsequent MutationObserver-driven init)
    // will keep retrying the click until Vue is finally bound and accepts
    // it. Driving the click via vueActive also lets it cleanly toggle off
    // when the user clears NOME/NÚMERO while the addon is on.
    var radioWrapper = currentRadio.closest(".tee-radio");
    var vueActive = !!(
      radioWrapper && radioWrapper.classList.contains("active")
    );

    if (shouldBeChecked && !vueActive) {
      clickWithoutStealingFocus(getClickTarget(currentRadio));
    } else if (!shouldBeChecked && vueActive) {
      clickWithoutStealingFocus(getClickTarget(currentRadio));
      if (currentRadio.checked) {
        currentRadio.checked = false;
        currentRadio.setAttribute("aria-checked", "false");
        if (radioWrapper) radioWrapper.classList.remove("active");
        currentRadio.dispatchEvent(new Event("input", { bubbles: true }));
        currentRadio.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  function initAddonChargeToggle(form) {
    // Heal-on-rebuild: if we previously initialised this wrapper but the
    // NOME input we bound to is no longer a descendant of the wrapper,
    // TeeInBlue has destroyed and recreated the Personalização fields
    // (typical after a Personalização <-> Jogador round-trip). Clear the
    // dataset guard so the rest of this function re-attaches listeners to
    // the fresh inputs.
    if (
      form &&
      form.dataset &&
      form.dataset.addonChargeInit === "1" &&
      form._tbBoundNameInput &&
      !form.contains(form._tbBoundNameInput)
    ) {
      delete form.dataset.addonChargeInit;
    }

    if (!form || !form.querySelector) return;
    if (form.dataset && form.dataset.addonChargeInit === "1") return;

    var bindingNodes = getActiveAddonChargeNodes(form);
    var nameInput = bindingNodes.nameInput;
    var numberInput = bindingNodes.numberInput;
    var addonRadio = bindingNodes.addonRadio;

    if (!addonRadio || (!nameInput && !numberInput)) {
      return;
    }

    if (form.dataset) form.dataset.addonChargeInit = "1";

    function onInputChange() {
      syncAddonChargeForForm(form);
    }

    [nameInput, numberInput].forEach(function (input) {
      if (!input) return;
      input.addEventListener("input", onInputChange);
      input.addEventListener("change", onInputChange);
    });

    // Stash the bound element identities on the wrapper itself so the next
    // observer-driven invocation can detect a TeeInBlue rebuild (see
    // heal-on-rebuild block at the top of this function).
    form._tbBoundNameInput = nameInput;
    form._tbBoundNumberInput = numberInput;
    form._tbBoundAddonRadio = addonRadio;

    syncAddonChargeForForm(form);
  }

  function scanAndInitAddonCharge() {
    var wrappers = document.querySelectorAll(".tee-campaign-form-wrapper");
    wrappers.forEach(initAddonChargeToggle);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scanAndInitAddonCharge);
  } else {
    scanAndInitAddonCharge();
  }

  var addonChargeScanScheduled = false;
  var addonChargeObserver = new MutationObserver(function () {
    if (addonChargeScanScheduled) return;
    addonChargeScanScheduled = true;
    setTimeout(function () {
      addonChargeScanScheduled = false;
      scanAndInitAddonCharge();
    }, 50);
  });
  addonChargeObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  function onTemplateRadioChange(e) {
    var t = e.target;
    if (
      !t ||
      !t.matches ||
      !t.matches(".tee-field--template input.tee-checkbox-input")
    ) {
      return;
    }
    // Defer past Vue's rebuild + mount of the new Personalizacao layer so
    // our TICK click lands on a Vue-bound .tee-radio wrapper (otherwise
    // the click flips radio.checked natively but TeeInBlue's store stays
    // off and .tee-price-addons is never rendered).
    setTimeout(function () {
      document
        .querySelectorAll(".tee-campaign-form-wrapper")
        .forEach(function (form) {
          syncAddonChargeForForm(form);
        });
    }, 150);
  }
  document.body.addEventListener("change", onTemplateRadioChange);

  /* ============================================================
   Price Changes TeeInBlue
   ============================================================ */

  // Add add-on price to main price
  // For campaign v2
  function isTeeinbluePlatformProduct() {
    return !!(
      window.teeinblueCampaign &&
      window.teeinblueCampaign.isPlatformProduct === true
    );
  }

  function parseMoneyText(text) {
    if (!text) return null;

    var match = String(text).match(/-?\d[\d\s.,]*/);
    if (!match) return null;

    var raw = match[0].replace(/\s+/g, "");
    var value = normalizeMoneyNumber(raw);

    return {
      value: value,
      raw: raw,
      prefix: text.slice(0, match.index),
      suffix: text.slice(match.index + match[0].length),
      meta: detectMoneyFormat(raw),
    };
  }

  function normalizeMoneyNumber(raw) {
    var value = String(raw || "").replace(/\s+/g, "");
    var lastComma = value.lastIndexOf(",");
    var lastDot = value.lastIndexOf(".");
    var decimalSep = "";

    if (lastComma !== -1 && lastDot !== -1) {
      decimalSep = lastComma > lastDot ? "," : ".";
    } else if (lastComma !== -1) {
      decimalSep = value.length - lastComma - 1 <= 2 ? "," : "";
    } else if (lastDot !== -1) {
      decimalSep = value.length - lastDot - 1 <= 2 ? "." : "";
    }

    if (decimalSep) {
      var thousandSep = decimalSep === "," ? "." : ",";
      value = value.split(thousandSep).join("");
      if (decimalSep === ",") value = value.replace(",", ".");
    } else {
      value = value.replace(/[.,]/g, "");
    }

    return Number(value) || 0;
  }

  function detectMoneyFormat(raw) {
    var value = String(raw || "").replace(/\s+/g, "");
    var lastComma = value.lastIndexOf(",");
    var lastDot = value.lastIndexOf(".");
    var decimalSep = "";
    var decimals = 0;
    var thousandSep = "";

    if (lastComma !== -1 && lastDot !== -1) {
      decimalSep = lastComma > lastDot ? "," : ".";
      thousandSep = decimalSep === "," ? "." : ",";
    } else if (lastComma !== -1) {
      if (value.length - lastComma - 1 <= 2) {
        decimalSep = ",";
      } else {
        thousandSep = ",";
      }
    } else if (lastDot !== -1) {
      if (value.length - lastDot - 1 <= 2) {
        decimalSep = ".";
      } else {
        thousandSep = ".";
      }
    }

    if (decimalSep) {
      decimals = value.split(decimalSep).pop().length;
    }

    return {
      decimalSep: decimalSep || ",",
      thousandSep: thousandSep,
      decimals: decimals,
    };
  }

  function formatMoneyLike(value, sourceText) {
    var parsedSource = parseMoneyText(sourceText);
    if (!parsedSource) return sourceText;

    var meta = parsedSource.meta;
    var absValue = Math.abs(Number(value) || 0);
    var fixed = absValue.toFixed(meta.decimals);
    var parts = fixed.split(".");
    var intPart = parts[0];
    var fracPart = parts[1] || "";

    if (meta.thousandSep) {
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, meta.thousandSep);
    }

    var formattedNumber = intPart;
    if (meta.decimals > 0) {
      formattedNumber += meta.decimalSep + fracPart;
    }

    return parsedSource.prefix + formattedNumber + parsedSource.suffix;
  }

  function isVisibleElement(element) {
    if (!element || !element.isConnected) return false;
    var style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") return false;
    return element.getClientRects().length > 0;
  }

  function getActiveAddonChargeNodes(form) {
    if (!form || !form.querySelectorAll) {
      return { nameInput: null, numberInput: null, addonRadio: null };
    }

    function pickVisibleInput(selector) {
      var matches = form.querySelectorAll(selector);
      var firstMatch = null;
      for (var i = 0; i < matches.length; i++) {
        if (!firstMatch) firstMatch = matches[i];
        if (isVisibleElement(matches[i])) return matches[i];
      }
      return firstMatch;
    }

    // The .s-personalization-addon-charge <input type="radio"> is intentionally
    // visually hidden by TeeInBlue's CSS in favour of a styled .tee-radio
    // wrapper. Visibility-check the wrapper instead of the input.
    function pickVisibleRadio(selector) {
      var matches = form.querySelectorAll(selector);
      var firstMatch = null;
      for (var i = 0; i < matches.length; i++) {
        if (!firstMatch) firstMatch = matches[i];
        var wrapper = matches[i].closest(".tee-radio");
        if (wrapper && isVisibleElement(wrapper)) return matches[i];
      }
      return firstMatch;
    }

    return {
      nameInput: pickVisibleInput(".s-personalization-name input"),
      numberInput: pickVisibleInput(".s-personalization-number input"),
      addonRadio: pickVisibleRadio(
        '.s-personalization-addon-charge input[type="radio"]',
      ),
    };
  }

  function getSelectedAddonTotal() {
    var nodes = document.querySelectorAll(
      ".tee-price-addons strong, .tee-price-addon strong",
    );
    var picked = null;
    for (var i = 0; i < nodes.length; i++) {
      if (!picked) picked = nodes[i];
      if (isVisibleElement(nodes[i])) {
        picked = nodes[i];
        break;
      }
    }
    if (!picked) return 0;
    var parsed = parseMoneyText(picked.textContent);
    return parsed ? parsed.value : 0;
  }

  function getScpDisplayPriceContainer() {
    var form = document.querySelector(".tee-campaign-form-wrapper");
    if (form) {
      var pdp = form.closest(".m-main-product");
      if (pdp) return pdp;
    }
    return document;
  }

  function getScpDisplayPriceElements() {
    var container = getScpDisplayPriceContainer();
    var selectors = [
      ".m-price--scp-custom .m-price__regular .m-price-item--regular",
      ".m-price--scp-custom .m-price__sale .m-price-item--sale",
      ".m-price--scp-custom .m-price__sale s.m-price-item--regular",
      ".m-price--scp-custom .m-price__scp-secondary .m-price-item--member",
    ];

    var results = [];
    selectors.forEach(function (selector) {
      container.querySelectorAll(selector).forEach(function (el) {
        if (isVisibleElement(el)) results.push(el);
      });
    });
    return results;
  }

  function syncTeeAddonPrices() {
    if (!isTeeinbluePlatformProduct()) return;

    var addonTotal = getSelectedAddonTotal();

    getScpDisplayPriceElements().forEach(function (priceEl) {
      var currentText = priceEl.textContent.trim();
      var currentParsed = parseMoneyText(currentText);
      if (!currentParsed) return;

      var storedBase = priceEl.dataset.tbBasePrice
        ? Number(priceEl.dataset.tbBasePrice)
        : null;
      var storedAddon = priceEl.dataset.tbAddonTotal
        ? Number(priceEl.dataset.tbAddonTotal)
        : 0;

      if (
        storedBase === null ||
        Math.abs(currentParsed.value - (storedBase + storedAddon)) > 0.001
      ) {
        storedBase = currentParsed.value;
      }

      var nextValue = storedBase + addonTotal;
      var nextText = formatMoneyLike(nextValue, currentText);

      priceEl.dataset.tbBasePrice = String(storedBase);
      priceEl.dataset.tbAddonTotal = String(addonTotal);

      if (priceEl.textContent.trim() !== nextText) {
        priceEl.textContent = nextText;
      }
    });
  }

  function scheduleTeeAddonPriceSync() {
    if (scheduleTeeAddonPriceSync._raf) return;

    scheduleTeeAddonPriceSync._raf = requestAnimationFrame(function () {
      scheduleTeeAddonPriceSync._raf = null;
      syncTeeAddonPrices();
    });
  }

  function observeTeeAddonPriceChanges() {
    if (observeTeeAddonPriceChanges._started) return;
    observeTeeAddonPriceChanges._started = true;

    var observer = new MutationObserver(function () {
      scheduleTeeAddonPriceSync();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden"],
    });

    observeTeeAddonPriceChanges._observer = observer;

    document.addEventListener("DOMContentLoaded", scheduleTeeAddonPriceSync);
    window.addEventListener("load", scheduleTeeAddonPriceSync);

    scheduleTeeAddonPriceSync();
  }

  function waitAndStartTeeAddonPriceSync() {
    if (waitAndStartTeeAddonPriceSync._started) return;
    waitAndStartTeeAddonPriceSync._started = true;

    observeTeeAddonPriceChanges();

    var attempts = 0;
    var maxAttempts = 120;

    var timer = setInterval(function () {
      attempts += 1;
      scheduleTeeAddonPriceSync();

      if (isTeeinbluePlatformProduct() || attempts >= maxAttempts) {
        clearInterval(timer);
      }
    }, 250);

    waitAndStartTeeAddonPriceSync._timer = timer;
  }

  waitAndStartTeeAddonPriceSync();
})();
